import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CalendarCommonModule } from 'angular-calendar';
import { Observable, tap, filter, take, first } from 'rxjs';
import { USER_ROLE } from 'src/app/constants/settingContants';
import { ProfileImage } from 'src/app/interfaces/profile-image';
import {
    Chat,
    Thread,
    ChatItemMessage,
    ChatItem,
} from 'src/app/models/ai-chat-models/chat.model';
import { Me } from 'src/app/models/me.model';
import { AiChatService } from 'src/app/services/ai-chat-service';
import { AppStateService } from 'src/app/services/app-state-service';
import { UserService } from 'src/app/services/user.service';
import { ChatControlsComponent } from '../chat-controls/chat-controls.component';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { TypingIndicatorComponent } from '../typing-indicator/typing-indicator.component';
import { MatCardModule } from '@angular/material/card';

@Component({
    selector: 'app-chat-dialog',
    standalone: true,
    imports: [
        ChatMessageComponent,
        ChatControlsComponent,
        TypingIndicatorComponent,
        NgFor,
        NgIf,
        AsyncPipe,
        MatIconModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        CalendarCommonModule,
        MatIconModule,
        MatButtonModule,
        MatToolbarModule,
        MatCardModule,
        MatDialogModule,
    ],
    templateUrl: './chat-dialog.component.html',
    styleUrl: './chat-dialog.component.scss',
})
export class ChatDialogComponent implements OnInit {
    @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
    @Input() height: string;
    @Input() width: string;
    private _chatHasInitialized = false;
    protected chat$: Observable<Chat>;
    protected threads$: Observable<Thread[]>;
    protected aiBuffering$: Observable<boolean>;
    protected threadBuffering$: Observable<boolean>;
    protected newThreadBeingSaved$: Observable<boolean>;
    protected chatLoading$: Observable<boolean>;
    protected localUser: { userData: Me; photo: string } = {
        userData: {} as Me,
        photo: '',
    };
    protected newMessage: boolean = false;
    protected messages: ChatItemMessage[] = [];
    protected chat: Chat;
    protected threads: Thread[];
    protected threadMenuOpen: boolean = false;

    constructor(
        private userService: UserService,
        private aiChatService: AiChatService,
        private appStateService: AppStateService,
        private dialogRef: MatDialogRef<ChatDialogComponent>,
    ) {}

    ngOnInit(): void {
        this.appStateService.me$.subscribe((me: Me) => {
            if (me && (me?.id ?? '').length > 0) {
                this.localUser.userData = me;
                this.userService
                    .get(['profile-image'])
                    .subscribe((profileImage: ProfileImage) => {
                        this.localUser.photo = profileImage.base64Value;
                    });
            }
        });

        this.chat$ = this.aiChatService.chat.pipe(
            tap((chat: Chat) => this.integrateNewMessages(chat)),
        );

        this.chat$
            .pipe(
                filter((chat: Chat) => chat !== null),
                take(1),
            )
            .subscribe(() => (this._chatHasInitialized = true));

        this.aiBuffering$ = this.aiChatService.aiBuffering;
        this.threadBuffering$ = this.aiChatService.threadBuffering;
        this.newThreadBeingSaved$ = this.aiChatService.newThreadSaving;
        this.chatLoading$ = this.aiChatService.chatLoading;
    }

    ngAfterViewChecked(): void {
        if (this.newMessage) {
            this.newMessage = false;
            setTimeout(() => this.scrollBottom());
        }
    }

    ngOnDestroy(): void {
        if (this.chat != null) {
            this.aiChatService.manageThreads();
        }
    }

    get chatHasInitialized(): boolean {
        return this._chatHasInitialized;
    }

    protected sendMessage(message: string): void {
        this.aiChatService.sendMessage(message);
    }

    private integrateNewMessages(chat: Chat): void {
        this.chat = chat;
        if (chat === null) {
            return;
        }

        this.newMessage = true;
        const temporaryMessage: ChatItemMessage = chat.completeChat.find(
            (chatItem: ChatItem) => chatItem.message.id.startsWith('temp-'),
        )?.message;

        if (
            temporaryMessage != undefined &&
            !this.messages.find(
                (message: ChatItemMessage) =>
                    message.id.startsWith('temp-') &&
                    message.role === USER_ROLE,
            )
        ) {
            this.messages.push(temporaryMessage);
            return;
        } else {
            const tempMessageExists = this.messages.find(
                (message: ChatItemMessage) =>
                    message.id.startsWith('temp-') &&
                    message.role === USER_ROLE,
            );

            if (tempMessageExists) {
                this.messages.pop();
            }

            if (this.messages.length + 1 > chat.completeChat.length) {
                this.messages = [];
            }

            chat.completeChat.forEach((chatItem: ChatItem) => {
                if (
                    !this.messages.some(
                        (message: ChatItemMessage) =>
                            message.id === chatItem.message.id,
                    )
                ) {
                    this.messages.push(chatItem.message);
                }
            });
        }
    }

    protected trackByCreated(index: number, message: ChatItemMessage): string {
        return message.createdAt;
    }

    private scrollBottom(): void {
        try {
            this.scrollContainer.nativeElement.scrollTop =
                this.scrollContainer.nativeElement.scrollHeight;
        } catch (err) {}
    }

    closeDialog() {
        this.dialogRef.close();
    }
}
